import { type FC, useMemo, useState } from 'react'
import { JsonLd } from 'react-schemaorg'
import { type VideoObject } from 'schema-dts'

import { type PimProductImageV11 } from '@redteclab/api/clients/bully'

import { useCdnMediaServer } from '../../../cdn'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC,
} from '../../../image'
import { productAccessorGetImages } from '../../model/productAccessor/productAccessorGetImages'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { getVideoSchema } from '../../seo/schemas/getVideoSchema'
import { useProductPageContext } from '../productPageContext'

import { ProductCarouselGalleryWithThumbnails } from './ProductCarouselGalleryWithThumbnails'
import { ProductCarouselLargeGalleryModal } from './ProductCarouselLargeGalleryModal'
import { type ProductCarouselProps } from './ProductCarouselProps'

const useProductImages = (): PimProductImageV11[] => {
  const { product } = useProductPageContext()

  const { setMediaServerOrigin } = useCdnMediaServer()

  const fallbackImageUrl = setMediaServerOrigin(
    productAccessorIsPrescriptionProduct(product)
      ? IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC
      : IMAGE_PRODUCT_FALLBACK_SRC,
  )

  return useMemo((): PimProductImageV11[] => {
    const productImages = productAccessorGetImages(product)

    if (productImages.length === 0) {
      return [
        {
          public_url: fallbackImageUrl,
        },
      ]
    }

    return productImages
  }, [fallbackImageUrl, product])
}

const ProductCarousel: FC<ProductCarouselProps> = ({
  productName,
  videos: videosProp,
}) => {
  const videos = useMemo(() => videosProp ?? [], [videosProp])

  const imagesToShow = useProductImages()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showLargeGalleryModal, setShowLargeGalleryModal] = useState(false)
  const [
    forceLargeGalleryToJumpToFirstVideo,
    setForceLargeGalleryToJumpToFirstVideo,
  ] = useState(false)
  const [playVideoOnScroll, setPlayVideoOnScroll] = useState(false)

  const handleOnModalClose = (): void => {
    setShowLargeGalleryModal(false)
    setForceLargeGalleryToJumpToFirstVideo(false)
  }

  const handleIndexChanged = (index: number): void => {
    setCurrentImageIndex(index)
  }

  const handleVideoBadgeClick = (scrollToVideo?: boolean): void => {
    if (scrollToVideo) {
      setPlayVideoOnScroll(true)
    } else {
      setForceLargeGalleryToJumpToFirstVideo(true)
      setShowLargeGalleryModal(true)
    }
  }

  return (
    <div className="relative flex flex-col items-center p-7 pb-3.5 tablet:px-20">
      <ProductCarouselGalleryWithThumbnails
        images={imagesToShow}
        initialImageIndex={0}
        onIndexChanged={handleIndexChanged}
        onItemClick={(): void => {
          setShowLargeGalleryModal(true)
        }}
        onVideoBadgeClicked={handleVideoBadgeClick}
        playVideoOnScrolledIntoView={playVideoOnScroll}
        productName={productName}
        showSimilarImagesNotice
        showVideoBadgeIfVideoAvailable
        thumbnails="all"
        videos={videos}
        zoomEnabled
      />
      {videos.map((video) => (
        <JsonLd<VideoObject>
          item={getVideoSchema(productName, video)}
          key={video.code}
        />
      ))}
      <ProductCarouselLargeGalleryModal
        images={imagesToShow}
        initialIndex={
          forceLargeGalleryToJumpToFirstVideo && videos.length > 0
            ? imagesToShow.length
            : currentImageIndex
        }
        isOpen={showLargeGalleryModal}
        onClose={handleOnModalClose}
        playVideoOnScrolledIntoView={
          forceLargeGalleryToJumpToFirstVideo ||
          currentImageIndex >= imagesToShow.length
        }
        productName={productName}
        videos={videos}
      />
    </div>
  )
}

export { ProductCarousel }
